import React from 'react';

import { useRouter } from 'next/router';
import styled from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';

import { mobile } from '../../styles';

const SizeResultContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.marble};
  padding: ${({ theme }) => theme.spacing.small}px
    ${({ theme }) => theme.spacing.tiny}px;
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.medium}px;
`;

const EmptySizeResult = styled(SizeResultContainer)`
  ${({ theme }) => theme.subHeader.variants.subHeading3.textStyles}
  padding: ${({ theme }) => theme.spacing.small}px;
  font-weight: 500;
`;

const SizeHeader = styled.h3`
  ${({ theme }) => theme.paragraph.variants.paragraph1Uppercase.textStyles}
  margin-bottom: ${({ theme }) => theme.spacing.small}px;
  text-align: center;
`;

const SizeValue = styled.div`
  ${({ theme }) => theme.header.variants.h1Alt.textStyles}
  color: ${({ theme }) => theme.colors.black};
  padding: ${({ theme }) => theme.spacing.tiny}px;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing.medium}px;
  gap: ${({ theme }) => theme.spacing.small}px;

  ${mobile`
    flex-direction: column;
  `}
`;

const ShopButton = styled.button`
  ${({ theme }) => theme.button.variants.primaryDark.buttonStyle}
  flex: 1;
`;

const SaveButton = styled.button`
  ${({ theme }) => theme.button.variants.primaryLavender.buttonStyle}
  flex: 1;
`;

const Paragraph = styled.p`
  margin: ${({ theme }) => theme.spacing.small}px
    ${({ theme }) => theme.spacing.none}px;
`;

type HandleShopMySizeCallback = (arg: {
  bra: string;
  bralette: string;
  sport: string;
}) => void;

interface SizeResultProps {
  calculatedSize: string;
  equivalentSize: string;
  saveMySize: () => void;
  isLoggedIn: boolean;
  handleShopMySize: HandleShopMySizeCallback;
}

const SizeResult: React.FC<SizeResultProps> = ({
  calculatedSize,
  equivalentSize,
  saveMySize,
  isLoggedIn,
  handleShopMySize: handleShopMySizeOnSubmit,
}) => {
  const router = useRouter();

  const handleShopMySize = event => {
    event.preventDefault();
    if (typeof handleShopMySizeOnSubmit === 'function') {
      handleShopMySizeOnSubmit({
        bra: calculatedSize,
        bralette: equivalentSize,
        sport: equivalentSize,
      });
      return;
    }

    router.push({
      pathname: '/products/bras',
      query: {
        size_bra: calculatedSize,
        size_bralette: equivalentSize,
        size_sport: equivalentSize,
        my_size: false,
      },
    });
  };

  if (calculatedSize === '') {
    return (
      <EmptySizeResult>
        <FormattedMessage
          id="fit_guide.no_size_found"
          defaultMessage={
            'Sorry, there is no result available for your size at ' +
            'this time.'
          }
        />
      </EmptySizeResult>
    );
  }

  return (
    <>
      <SizeResultContainer>
        <SizeHeader>
          <FormattedMessage
            id="fit_guide.your_size_is"
            defaultMessage="Your size is:"
          />
        </SizeHeader>
        <SizeValue>
          {calculatedSize} | {equivalentSize}
        </SizeValue>
      </SizeResultContainer>
      <ActionButtonsContainer>
        <ShopButton onClick={handleShopMySize}>
          <FormattedMessage
            id="fit_guide.shop_my_size_bras"
            defaultMessage="Shop my size bras"
          />
        </ShopButton>
        {isLoggedIn && (
          <SaveButton onClick={saveMySize}>
            <FormattedMessage
              id="fit_guide.save_my_size"
              defaultMessage="Save my size"
            />
          </SaveButton>
        )}
      </ActionButtonsContainer>
      {isLoggedIn && (
        <Paragraph>
          <FormattedMessage
            id="fit_guide.size_will_be_saved"
            defaultMessage={
              '*Your current size will be saved the your profile, ' +
              'and all products you see will be displayed with this ' +
              'size selected by default. You can change this anytime.'
            }
          />
        </Paragraph>
      )}
    </>
  );
};

export default SizeResult;
